import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from 'state/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const columns = [
  { field: 'crewCode', headerName: 'Code', width: 100 },
  { field: 'crewName', headerName: 'Name', flex: 2, minWidth: 160 },
  { field: 'rank', headerName: 'Rank', width: 90 },
  { field: 'location', headerName: 'Location', flex: 1, minWidth: 100 },
];

const EMPTY_SELECTION = { type: 'include', ids: new Set() };

const ImportCrewDialog = ({ open, onClose, onImportComplete }) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [availableCrew, setAvailableCrew] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectionModel, setSelectionModel] = useState(EMPTY_SELECTION);

  const selectedCount = selectionModel.type === 'include'
    ? selectionModel.ids.size
    : availableCrew.length - selectionModel.ids.size;

  useEffect(() => {
    if (!open) { setSelectionModel(EMPTY_SELECTION); return; }
    setLoading(true);
    api.get('/employees/available-crew')
      .then(({ data }) => setAvailableCrew(data))
      .catch(() => toast.error('Failed to load available crew.'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleImport = async () => {
    if (!selectedCount) return;
    setImporting(true);
    try {
      const crewCodes = selectionModel.type === 'include'
        ? [...selectionModel.ids]
        : availableCrew.map(c => c.crewCode).filter(code => !selectionModel.ids.has(code));
      const { data } = await api.post('/employees/import-from-crew', { crewCodes });
      const { imported = [], skipped = [], failed = [] } = data;
      toast.success(`Imported: ${imported.length}${skipped.length ? `, Skipped: ${skipped.length}` : ''}`);
      if (failed.length) toast.error(`Failed: ${failed.length} - check console`);
      if (failed.length) console.error('Import failures:', failed);
      onImportComplete?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUpload color="primary" />
        Import Crew Members as Employees
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Importing creates an Employee record with <strong>employeeCode = crewCode</strong>.
          Existing training history is automatically preserved.
        </Alert>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : availableCrew.length === 0 ? (
          <Typography color="text.secondary" align="center" py={4}>
            All active crew members have already been imported.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {availableCrew.length} crew member(s) available · {selectedCount} selected
            </Typography>
            <Box height={400}>
              <DataGrid
                getRowId={(r) => r.crewCode}
                rows={availableCrew}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={setSelectionModel}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                density="compact"
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={importing}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
          onClick={handleImport}
          disabled={!selectedCount || importing}
        >
          {importing ? 'Importing...' : `Import (${selectedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCrewDialog;
